/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useRef, createRef } from 'react';
import Grid from '@material-ui/core/Grid';
import {
    object, string, arrayOf, shape, array,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {  useDispatch } from 'react-redux';
import { setCountry } from '../../../../state/ducks/App/App-Actions';
import customBreakpoints from '../../../helpers/customBreakpoints';
import triggerPopChatEvent from '../../MultiBrandChatController/AigoChatWidgetWrapperV2/popOpenChat';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';

const useStyles = makeStyles((theme) => ({
    shopBlock: {
        textAlign: 'center',
        '& ul': {
            margin: '35px 0',
            display: 'flex',
            justifyContent: 'space-around',
            padding: '0 20px',
            flexWrap: 'wrap',
            [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
                margin: '10px',
                justifyContent: 'space-evenly',
                padding: '0',
            },
            '& li ': {
                listStyle: 'none',
                [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
                    width: '8%',
                    margin: '10px auto',
                },
            },

        },
    },
    shopTitle: {
        fontSize: '36px',
        lineHeight: '36px',
        textTransform: 'uppercase',
        fontWeight: 'bold',
        letterSpacing: '0.03em',
        margin: '0',
        [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
            fontSize: '18px',
        },
    },
    shopLink: {
        textDecoration: 'underline',
        cursor: 'pointer',
        textTransform: 'uppercase',
        fontSize: '20px',
        fontWeight: 'bold',
        color: theme.palette.primary.main || '#201648',
    },
    countryLetter: {
        fontSize: '20px',
        margin: '0',
        textTransform: 'uppercase',

    },
    countryContainer: {
        padding: '20px',
        width: '80%',
        margin: '10px auto 0',
        border: '1px solid #EDEDED',
        borderRadius: '3px',
        boxShadow: '0 2px 3px rgba(59,33,81,0.15), 0 2px 2px rgba(59,33,81,0.15)',
        [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
            width: '90%',
        },
        '& ul': {
            padding: '0',
            display: 'flex',
            width: '75%',
            margin: '15px auto 0',
            justifyContent: 'center',
            flexWrap: 'wrap',
            [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
                width: '100%',
                justifyContent: 'space-evenly',
            },
            '& li': {
                listStyle: 'none',
                flexBasis: '25%',
                marginBottom: '15px',
                [theme.breakpoints.down(customBreakpoints.tabLandscape)]: {
                    flexBasis: '26%',
                },
            },
        },
    },
    countryFlag: {
        margin: '0 auto',
        padding: '0',
    },
    callDisclaimer: {
        margin: ' 15px 0',
        letterSpacing: '0.03em',
        fontSize: '14px',
        '& strong': {
            color: theme.palette.primary.main || '#201648',
        },
    },
    countryName: {
        fontSize: '14px',
        letterSpacing: '0.03em',
    },
    countryNameList: {
        color: theme.palette.primary.main || '#201648',
    },
    countryLink: {
        color: theme.palette.primary.main || '#201648',
    },
}));

const ShopByCountry = ({
    shopLink, heading, styleCSS, countries,
    operatingSystem, currentBrand, featureFlags, customerData, chatLinkStyle,
}) => {
    let scrollOn = useRef(null);
    const dispatch = useDispatch();
    const classes = useStyles();
    const handleClick = (value) => {
        scrollOn[value].current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    };

    const countryDetailDesign = (list, i) => {
        const { country_name, url, country_flag } = list;
        return (
            <li key={['_', i].join('')}>
                <a className={classes.countryLink} href={url} onClick={() => dispatch(setCountry(country_name.toLowerCase()))}>
                    <img className={`${classes.countryFlag} ${styleCSS.trendingProductImage}`} src={`https://cdn1.1800flowers.com/wcsstore/Flowers/images/banners/international/flags/${country_flag}.gif?height=46&width=62`} alt={list.country_name} />
                    <div className={`${classes.countryNameList} ${classes.countryName}`}>{country_name}</div>
                </a>
            </li>

        );
    };

    const countryLayout = (data, call_disclaimer) => {
        const {
            countryLetter, country_detail: countryDetail, call_number, countries_names_list,
        } = data;
        return (
            <div ref={scrollOn[countryLetter]} key={countryLetter} className={classes.countryContainer}>
                <h3 className={classes.countryLetter}>{countryLetter}</h3>
                {countryDetail.length > 0 && (
                    <ul>
                        {
                            countryDetail.map((list, i) => (
                                countryDetailDesign(list, i)
                            ))

                        }

                    </ul>
                )}
                <div className={classes.callDisclaimer}> If you need assistance placing your international order, please
                    <span
                        onClick={() => {
                            dispatch(trackEvent({
                                eventCategory: 'Chat',
                                eventAction: 'Click',
                                eventLabel: 'International Assistance',
                            }));
                            triggerPopChatEvent({}, operatingSystem, currentBrand, featureFlags, customerData);
                        }}
                        onKeyDown={() => { }}
                        role="button"
                        tabIndex="0"
                        alt="chat"
                        style={chatLinkStyle}
                    >chat
                    </span>with us!
                </div>
                <div className={classes.countryName}>{countries_names_list}</div>
            </div>
        );
    };

    const { countries_list, call_disclaimer } = countries;
    scrollOn = countries_list.reduce((acc, value) => {
        acc[value.countryLetter] = createRef();
        return acc;
    }, {});
    return (
        <>
            <Grid container className={styleCSS.container}>
                <Grid item xs={12} className={classes.shopBlock}>
                    <div>
                        <h2 className={classes.shopTitle}>{heading}</h2>
                    </div>
                    <ul>
                        {shopLink.map((list) => (
                            <li key={list}>
                                <div aria-hidden className={classes.shopLink} onKeyPress={() => { }} onClick={() => handleClick(list)}>{list}</div>
                            </li>
                        ))}
                    </ul>
                </Grid>
                <Grid item xs={12} className={classes.shopBlock}>
                    {countries_list.map((data) => (
                        countryLayout(data, call_disclaimer)
                    ))}
                </Grid>
            </Grid>
        </>
    );
};
ShopByCountry.propTypes = {
    styleCSS: object.isRequired,
    shopLink: array.isRequired,
    heading: string.isRequired,
    countries: shape({
        call_disclaimer: string.isRequired,
        countries_list: arrayOf(
            shape({
                countryLetter: string.isRequired,
                countries_names_list: string.isRequired,
                call_number: string.isRequired,
                countryDetail: arrayOf(
                    shape({
                        country_name: string.isRequired,
                        url: string.isRequired,
                        country_flag: string.isRequired,
                    }),
                ),
            }),
        ),
    }).isRequired,
    currentBrand: string.isRequired,
    featureFlags: object.isRequired,
    operatingSystem: string.isRequired,
    customerData: object.isRequired,
    chatLinkStyle: object.isRequired,
};

export default ShopByCountry;
